import React, { useEffect, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2102RoleFormExtendedHandlers } from "../../../U2102RoleForm";
import { NS, U2102ROLES } from "../../../../U2102";
import { emptyLogicalUnit, type U2102LogicalUnit, type U2102RoleLogicalUnit } from "../../../../U2102Service";
import { u2102RoleActionHandler } from "./U2102RoleActionHandlers";
import U2102RoleActionEditDlg from "./U2102RoleActionEditDlg";
import U2102RoleActionDeleteDlg from "./U2102RoleActionDeleteDlg";
import { U2000StringArrayDisplay } from "../../../../../u2000Core/components/tables/display/U2000StringArrayDisplay";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";

// Typage des props du composant
interface U2102RoleActionsComponentProps {
  u2102RoleFormStateExtendedHandlers: U2102RoleFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "logicalUnitCode",
      label: t("lblLogicalUnitCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitDescription",
      label: t("lblLogicalUnitDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[27%]",
      type: "string",
      align: "left",
    },
    {
      name: "actions",
      label: t("lblActions", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[60%]",
      type: "string",
      align: "left",
      display: (column, value, row) => {
        return <U2000StringArrayDisplay column={column} value={value} row={row} />;
      },
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U2102RoleActionsBody: React.FC<U2102RoleActionsComponentProps> = ({ u2102RoleFormStateExtendedHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<U2102RoleLogicalUnit>();

  const validateRoleAction = (logicalUnit: U2102LogicalUnit) => {
    const result: Record<string, string> = {};
    if (logicalUnit.logicalUnitCode.trim() === "") {
      result["logicalUnitCode"] = "Logical Unit Code is required.";
    }
    return result;
  };

  const u2102RoleActionBL: U2000FormSBusinessHandlers<U2102LogicalUnit> = {
    newRecord: () => {
      return emptyLogicalUnit;
    },

    // validation du formulaire
    validateForm: validateRoleAction,
  };

  const u2102RoleActionformStateHandlers = useU2000FormStateHandlers<U2102LogicalUnit>({
    formState,
    formStateDispatch,
    businessHandlers: u2102RoleActionBL,
  });

  const u2102RoleActionHandlers = u2102RoleActionHandler({
    u2000AxiosAuthenticated,
    gridStateHandlers,
    t,
    formStateHandlers: u2102RoleActionformStateHandlers,
  });

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));

    // Initialiser les données du tableau
    const logicalUnitActions = u2102RoleFormStateExtendedHandlers.getData?.()?.actions as U2102RoleLogicalUnit[];
    gridStateHandlers.setData(Array.isArray(logicalUnitActions) ? logicalUnitActions : []);
    gridStateHandlers.setTotalRows(Array.isArray(logicalUnitActions) ? logicalUnitActions.length : 0);

    // Gérer les erreurs spécifiques au champ LogicalUnitActions
    const errorsField = u2102RoleFormStateExtendedHandlers.getOtherErrors()?.roleActions;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u2102RoleFormStateExtendedHandlers, t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u2102RoleActionHandlers.dataChangedEvent === 0) return;

    const logicalUnitActions = gridStateHandlers.getData();
    u2102RoleFormStateExtendedHandlers.setData({
      ...u2102RoleFormStateExtendedHandlers.getData?.(),
      actions: logicalUnitActions,
    });
  }, [u2102RoleActionHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2102RoleFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2102RoleFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false;
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u2102RoleActionHandlers.onAdd,
        hasRole: hasRole(U2102ROLES.ACTIONS),
        disabled: u2102RoleFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u2102RoleActionHandlers.onModify,
        hasRole: hasRole(U2102ROLES.ACTIONS),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u2102RoleActionHandlers.onDelete,
        hasRole: hasRole(U2102ROLES.ACTIONS),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u2102RoleActionHandlers]
  );

  // Render
  return (
    <>
      <U2102RoleActionEditDlg u2102RoleActionHandler={u2102RoleActionHandlers} t={t} NS={NS} />
      <U2102RoleActionDeleteDlg u2102RoleActionHandler={u2102RoleActionHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u2102RoleFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblRoleAction", {
          count: u2102RoleActionHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2102RoleActionsBody;
